import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError } from '../../services/apiClient';
import { sequenceService } from '../../services/sequenceService';
import type {
  OutboundEmailSnapshot,
  SequenceEnrollment,
  SequenceStepInstance,
} from '../../types/sequence';
import { useToast } from '../../contexts/ToastContext';
import {
  channelLabel,
  formatTouchDate,
  instanceStateLabel,
  nextTouchLine,
  stateChip,
} from './copy';
import StartSequenceButton from './StartSequenceButton';

interface Props {
  contactType: 'client' | 'lead';
  contactId: string;
  contactName: string;
}

const CHIP_TONES: Record<string, string> = {
  active: 'bg-primary/10 text-primary',
  paused: 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300',
  done: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300',
  stopped: 'bg-fg/10 text-fg-muted',
};

const StepRow: React.FC<{
  instance: SequenceStepInstance;
  snapshot?: OutboundEmailSnapshot;
}> = ({ instance, snapshot }) => {
  const [open, setOpen] = useState(false);
  const canExpand = instance.state === 'sent' && instance.channel === 'email' && Boolean(snapshot);

  return (
    <li className="rounded-lg border border-border">
      <div className="flex items-center justify-between px-3 py-2">
        <div className="text-sm text-fg">
          <span className="font-medium">{channelLabel(instance.channel)}</span>
          <span className="text-fg-muted">
            {' · '}
            {instance.state === 'pending'
              ? formatTouchDate(instance.scheduled_for)
              : instanceStateLabel(instance.state)}
            {instance.state === 'sent' && instance.sent_at ? ` ${formatTouchDate(instance.sent_at)}` : ''}
          </span>
          {instance.state === 'failed' && instance.failure_reason ? (
            <span className="ml-2 text-red-600 dark:text-red-400">{instance.failure_reason}</span>
          ) : null}
        </div>
        {canExpand ? (
          <button type="button" onClick={() => setOpen((o) => !o)} className="btn-surface-muted rounded-lg px-2 py-1 text-xs">
            {open ? 'Hide' : 'View email'}
          </button>
        ) : null}
      </div>
      {open && snapshot ? (
        <div className="border-t border-border bg-surface-muted px-3 py-2 text-sm">
          <p className="font-medium text-fg">{snapshot.subject_rendered}</p>
          <p className="mt-1 whitespace-pre-wrap text-fg-muted">{snapshot.body_rendered}</p>
        </div>
      ) : null}
    </li>
  );
};

const EnrollmentBlock: React.FC<{
  enrollment: SequenceEnrollment;
  snapshots: OutboundEmailSnapshot[];
  onChanged: () => void;
}> = ({ enrollment, snapshots, onChanged }) => {
  const { showToast } = useToast();
  const [busy, setBusy] = useState(false);
  const chip = stateChip(enrollment);
  const next = nextTouchLine(enrollment);

  const snapshotFor = useMemo(() => {
    const map = new Map<number, OutboundEmailSnapshot>();
    snapshots
      .filter((s) => s.sequence_id_snapshot === enrollment.sequence)
      .forEach((s) => map.set(s.step_index, s));
    return map;
  }, [snapshots, enrollment.sequence]);

  const act = useCallback(
    async (fn: () => Promise<unknown>, done: string) => {
      setBusy(true);
      try {
        await fn();
        showToast(done, 'success');
        onChanged();
      } catch (err) {
        showToast(err instanceof ApiError ? err.message : 'Action failed.', 'error');
      } finally {
        setBusy(false);
      }
    },
    [onChanged, showToast]
  );

  const nextPending = (enrollment.step_instances ?? []).find((i) => i.state === 'pending');

  return (
    <div className="rounded-xl border border-border bg-surface p-4">
      <div className="flex items-center justify-between">
        <div>
          <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${CHIP_TONES[chip.tone]}`}>
            {chip.label}
          </span>
          <span className="ml-2 text-sm text-fg-muted">{enrollment.sequence_name}</span>
        </div>
        {enrollment.state === 'active' ? (
          <div className="flex gap-1">
            <button
              type="button"
              disabled={busy}
              onClick={() => act(() => sequenceService.pauseEnrollment(enrollment.id), 'Sequence paused')}
              className="btn-surface-muted rounded-lg px-2 py-1 text-xs"
            >
              Pause
            </button>
            {nextPending ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => act(() => sequenceService.skipStep(enrollment.id, nextPending.step_index), 'Step skipped')}
                className="btn-surface-muted rounded-lg px-2 py-1 text-xs"
              >
                Skip this step
              </button>
            ) : null}
            <button
              type="button"
              disabled={busy}
              onClick={() => act(() => sequenceService.stopEnrollment(enrollment.id), 'Sequence stopped')}
              className="btn-surface-muted rounded-lg px-2 py-1 text-xs"
            >
              Stop
            </button>
          </div>
        ) : enrollment.state === 'paused' ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => act(() => sequenceService.resumeEnrollment(enrollment.id), 'Sequence resumed')}
            className="btn-secondary rounded-lg px-3 py-1 text-xs"
          >
            Resume
          </button>
        ) : null}
      </div>

      {next ? <p className="mt-2 text-sm text-fg-muted">{next}</p> : null}

      <ul className="mt-3 space-y-2">
        {(enrollment.step_instances ?? [])
          .slice()
          .sort((a, b) => a.step_index - b.step_index)
          .map((inst) => (
            <StepRow key={inst.id} instance={inst} snapshot={snapshotFor.get(inst.step_index)} />
          ))}
      </ul>
    </div>
  );
};

/** Contact-timeline sequence card (§9.4). Renders above the activity feed on contact detail views. */
const SequenceCard: React.FC<Props> = ({ contactType, contactId, contactName }) => {
  const [enrollments, setEnrollments] = useState<SequenceEnrollment[]>([]);
  const [snapshots, setSnapshots] = useState<OutboundEmailSnapshot[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const history = await sequenceService.contactHistory(contactType, contactId);
      setEnrollments(history.enrollments);
      setSnapshots(history.snapshots);
    } catch {
      // A contact with no history yields an empty card; failures stay quiet here.
    } finally {
      setLoading(false);
    }
  }, [contactType, contactId]);

  useEffect(() => {
    load();
  }, [load]);

  const hasActive = enrollments.some((e) => e.state === 'active' || e.state === 'paused');

  if (loading) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-fg">Sequences</h3>
        {!hasActive ? (
          <StartSequenceButton
            contact={{ type: contactType, id: contactId, name: contactName }}
            onEnrolled={load}
            className="btn-secondary rounded-lg px-3 py-1.5 text-xs"
          />
        ) : null}
      </div>

      {enrollments.length === 0 ? (
        <p className="rounded-xl border border-border bg-surface p-4 text-sm text-fg-muted">
          Not in a sequence yet.
        </p>
      ) : (
        enrollments.map((e) => (
          <EnrollmentBlock key={e.id} enrollment={e} snapshots={snapshots} onChanged={load} />
        ))
      )}
    </div>
  );
};

export default SequenceCard;
